//* Import react and dependencies
import { useEffect, useState, useCallback } from "react";

//* Import react-router hooks
import { useNavigate, useParams } from "react-router-dom";

//* Import react-icons
import { RiArrowGoBackLine } from "react-icons/ri";

//* Import moment
import moment from "moment";

//* Import components
import TabPanel from "../../../component/base/TabPanel";
import { Grid } from "../../../component/list/grid";
import { Alert } from "../../../component/list/alert";
import { Tab, Tabs } from "@material-ui/core";
import { TextReadMore } from "../../../component/base/TextReadMore";
import DefaultButton from "../../../component/base/DefaultButton";

//! Importing the useDemands hook
import { useDemands } from "../../../hooks/demand/useDemands";

//! Importing the useAttributions hook
import { useAttributions } from "../../../hooks/demand/attributions/useAtributions";

//? Importing types from useDemands.tsx
import { DemandsDetails } from "../../../hooks/demand/types/types";

//? Importing types from useAtributions.tsx
import { AttributionDetails } from "../../../hooks/demand/attributions/types/types";

//! Importing the LoadingSpinner component
import LoadingSpinner from "../../../component/base/LoadingSpinner";

//* Import libraries
var debounce = require("lodash.debounce");

/**
 * The `DemandsManagerView` component is responsible for managing and displaying the details of a specific demand,
 * including its attributions. It provides functionalities for searching, paginating, and deleting attributions.
 *
 * @component
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * ```tsx
 * <DemandsManagerView />
 * ```
 *
 * @remarks
 * This component uses several hooks and custom hooks to manage state and side effects:
 * - `useState` for managing local state.
 * - `useEffect` for fetching data and handling side effects.
 * - `useCallback` for memoizing functions.
 * - `useNavigate` and `useParams` from `react-router-dom` for navigation and accessing route parameters.
 * - Custom hooks `useDemands` and `useAttributions` for fetching and managing demands and attributions data.
 *
 * @function
 * @name DemandsManagerView
 *
 * @hook
 * @name useState
 * @description Manages local state for active tab, demand details, warning visibility, selected attribution ID, search input value, deletion state, total attributions, and just deleted state.
 *
 * @hook
 * @name useEffect
 * @description Fetches demand data based on route parameters and updates state accordingly. Also handles pagination and deletion side effects.
 *
 * @hook
 * @name useCallback
 * @description Memoizes functions for debounced search input changes and pagination changes.
 *
 * @hook
 * @name useNavigate
 * @description Provides navigation functionality.
 *
 * @hook
 * @name useParams
 * @description Accesses route parameters.
 *
 * @hook
 * @name useDemands
 * @description Custom hook for fetching and managing demand data.
 *
 * @hook
 * @name useAttributions
 * @description Custom hook for fetching and managing attributions data.
 *
 * @param {number} activeTab - The currently active tab index.
 * @param {DemandsDetails | null} demand - The details of the current demand.
 * @param {boolean} showWarning - Flag indicating whether the warning modal is visible.
 * @param {string | null} selectedId - The ID of the selected attribution for deletion.
 * @param {string} searchInputValue - The current value of the search input.
 * @param {boolean} isDeleting - Flag indicating whether an attribution is being deleted.
 * @param {number} totalAttributions - The total number of attributions.
 * @param {boolean} justDeleted - Flag indicating whether an attribution was just deleted.
 * @param {boolean} demandLoading - Flag indicating whether the demand data is loading.
 * @param {Function} getDemandById - Function to fetch demand data by ID.
 * @param {Function} deleteDemand - Function to delete a demand.
 * @param {Function} refetchDemands - Function to refetch demands data.
 * @param {Array} attributions - The list of attributions.
 * @param {boolean} attributionsLoading - Flag indicating whether the attributions data is loading.
 * @param {Function} attributionSearch - Function to search attributions.
 * @param {Function} setAttributionSearch - Function to set the search value for attributions.
 * @param {Object} pagination - The pagination object containing total items, total pages, and current page.
 * @param {Function} setPagination - Function to set the pagination state.
 * @param {Function} deleteAttribution - Function to delete an attribution.
 * @param {Function} debouncedSearch - Debounced function to handle search input changes.
 * @param {Function} handleSearchInputChange - Function to handle search input changes.
 * @param {Function} handleDelete - Function to handle deletion of an attribution.
 * @param {Function} handleChangePagination - Function to handle pagination changes.
 */
export function DemandsManagerView() {
  const [activeTab, setActiveTab] = useState(0);
  const [demand, setDemand] = useState<DemandsDetails | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalAttributions, setTotalAttributions] = useState<number>(0);
  const [justDeleted, setJustDeleted] = useState<boolean>(false);

  const navigate = useNavigate();
  const params = useParams();

  const token = localStorage.getItem("token") || "";

  const {
    loading: demandLoading,
    getDemandById,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refetch: refetchDemands,
  } = useDemands(token);

  const {
    attributions,
    loading: attributionsLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    search: attributionSearch,
    status: selectedStatus,
    setStatus: setSelectedStatus,
    setSearch: setAttributionSearch,
    pagination,
    setPagination,
    deleteAttribution,
  } = useAttributions(token, params.id);

  useEffect(() => {
    /**
     * Fetches demand data based on the provided `params.id`.
     * If `params.id` is available, it retrieves the demand data by calling `getDemandById` with the `params.id`.
     * Once the data is fetched, it updates the state with the retrieved demand data.
     *
     * @async
     * @function fetchData
     * @returns {Promise<void>} A promise that resolves when the data fetching and state update are complete.
     */
    async function fetchData() {
      if (params.id) {
        const data = await getDemandById(params.id);
        setDemand(data);
      }
    }
    fetchData();
  }, [params.id, getDemandById]);

  useEffect(() => {
    setTotalAttributions(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  useEffect(() => {
    if (
      justDeleted &&
      Array.isArray(attributions) &&
      attributions.length === 0 &&
      pagination.currentPage > 1
    ) {
      setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }));
      setJustDeleted(false);
    }
  }, [attributions, pagination.currentPage, setPagination, justDeleted]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setAttributionSearch(value);
      } else {
        setAttributionSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
    }, 500),
    [setAttributionSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDelete = async () => {
    try {
      if (selectedId) {
        setIsDeleting(true);
        await deleteAttribution(selectedId);
        setShowWarning(false);
        setSelectedId(null);
        setJustDeleted(true);
      } else {
        console.error("selectedId está undefined ou null.");
      }
    } catch (error) {
      console.error("Erro ao deletar atribuição:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangePagination = useCallback(
    async (value: number) => {
      setPagination((prev) => ({ ...prev, currentPage: value }));
    },
    [setPagination]
  );

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "NotStarted":
        return '<span style="color: #e31313; font-weight: bold;">Não Iniciado</span>';
      case "InProgress":
        return '<span style="color: orange; font-weight: bold;">Em Progresso</span>';
      case "Finished":
        return '<span style="color: green; font-weight: bold;">Finalizado</span>';
      default:
        return status;
    }
  };

  if (demandLoading || !demand || isDeleting) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "50px" }}
      >
        <LoadingSpinner loading />
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }} data-testid="demands-manager-view">
      <div className="card">
        <div className="card-body">
          <Alert
            title="Atenção"
            text="Ao deletar esta atribuição, ela não estará mais disponível no sistema"
            show_warning={showWarning}
            onCancel={() => setShowWarning(false)}
            onConfirm={handleDelete}
            data-testid="delete-attribution-alert"
          />

          <div>
            <div style={{ float: "right" }}>
              <DefaultButton
                bg="secondary"
                text="Voltar"
                icon={<RiArrowGoBackLine />}
                to={`/demands`}
                data-testid="back-button"
              />
            </div>
            <div className="flex jcsb">
              <h4 className="header" data-testid="demand-name">{demand.name}</h4>
            </div>

            {demand.date && (
              <TextReadMore
                title="Encerramento: "
                element="div"
                limit={1000}
                style={{ width: "100%", marginBottom: 10, whiteSpace: "pre" }}
                data-testid="demand-closing-date"
              >
                {moment(demand.date).format("DD/MM/YYYY HH:mm")}
              </TextReadMore>
            )}

            {demand.desc && (
              <TextReadMore
                title="Descrição: "
                element="div"
                limit={120}
                style={{ width: "100%", marginBottom: 10, whiteSpace: "pre" }}
                data-testid="demand-description"
              >
                {demand.desc ?? ""}
              </TextReadMore>
            )}

            {demand.tags && demand.tags.length > 0 && (
              <TextReadMore
                title="Palavras chave: "
                element="div"
                limit={120}
                style={{ width: "100%", marginBottom: 10, whiteSpace: "pre" }}
                data-testid="demand-tags"
              >
                {demand.tags.map((tag) => tag.name).join(", ")}
              </TextReadMore>
            )}

            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              style={{ backgroundColor: "#ececf1" }}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="on"
              data-testid="demand-tabs"
            >
              <Tab label="Atribuições de Questões" data-testid="attributions-tab" />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
              <Grid
                onChangeSearchInput={handleSearchInputChange}
                search_input_value={searchInputValue}
                loading_search={attributionsLoading || isDeleting}
                fields={[
                  { name: "description", type: "text", header: "Descrição" },
                  { name: "teacher", type: "text", header: "Professor" },
                  { name: "discipline", type: "text", header: "Disciplina" },
                  {
                    name: "questions",
                    type: "text",
                    header: "Questões Cadastradas",
                  },
                  { name: "status", type: "text", header: "Status" },
                ]}
                items={
                  Array.isArray(attributions)
                    ? attributions.map((attribution: AttributionDetails) => {
                        const questions = [];
                        if (attribution.quantityObjective !== 0) {
                          questions.push(
                            `Objetivas: ${attribution.quantityObjectiveFinish}/${attribution.quantityObjective}`
                          );
                        }
                        if (attribution.quantityDissertative !== 0) {
                          questions.push(
                            `Dissertativas: ${attribution.quantityDissertativeFinish}/${attribution.quantityDissertative}`
                          );
                        }
                        if (attribution.quantitySet !== 0) {
                          questions.push(
                            `Conjuntos: ${attribution.quantitySetFinish}/${attribution.quantitySet}`
                          );
                        }
                        return {
                          _id: attribution.id,
                          key: attribution.id,
                          description: attribution.description,
                          teacher: attribution.teacher.name,
                          discipline: attribution.discipline.name,
                          questions: questions.join("<br>"),
                          status: getStatusLabel(attribution.status),
                        };
                      })
                    : []
                }
                onManagerActionClick={(id) => {
                  navigate(`/demands/${params.id}/questions/${id}`);
                }}
                onDeleteActionClick={(id) => {
                  setSelectedId(id);
                  setShowWarning(true);
                }}
                onEditActionClick={(id) => {
                  navigate(`/demands/${params.id}/edit/${id}`);
                }}
                not_found_message="Nenhuma atribuição encontrada."
                loading_screen={isDeleting || attributionsLoading}
                button_text="Adicionar atribuições"
                button_link={`/demands/${params.id}/create`}
                search_text="Pesquisar pela descrição, nome do professor ou disciplina"
                pagination={{
                  last_page: pagination.totalPages,
                  current_page: pagination.currentPage,
                }}
                onChangePagination={handleChangePagination}
                top_text={`Aqui ficam as atribuições da demanda ${demand.name}`}
                onSearchButtonClick={() => {}}
                radioOptions={[
                  { label: "Todos", value: "all" },
                  { label: "Não Iniciado", value: "NotStarted" },
                  { label: "Em Processo", value: "InProgress" },
                  { label: "Finalizado", value: "Finished" },
                ]}
                selectedRadioValue={selectedStatus}
                onRadioChange={(value) => setSelectedStatus(value)}
                data-testid="attributions-grid"
              />
            </TabPanel>
          </div>
        </div>
      </div>
    </div>
  );
}
